import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../services/authService'
import {
  PlusIcon,
  PaperAirplaneIcon,
  TrashIcon,
  PencilIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  ChartBarIcon,
  DocumentTextIcon,
  EyeIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  FunnelIcon,
  ArrowPathIcon,
  SignalIcon,
  SignalSlashIcon,
  ClipboardDocumentListIcon,
  SwatchIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const CAMPAIGN_STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  scheduled: { label: 'Agendada', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  sending: { label: 'Enviando', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
  sent: { label: 'Enviada', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' }
}

const CHANNEL_CONFIG = {
  email: { label: 'Email', icon: EnvelopeIcon, color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300' },
  whatsapp: { label: 'WhatsApp', icon: ChatBubbleLeftRightIcon, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' },
  multi: { label: 'Multicanal', icon: MegaphoneIcon, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' }
}

const TEMPLATE_CATEGORIES = {
  transacional: { label: 'Transacional', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  marketing: { label: 'Marketing', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300' },
  automacao: { label: 'Automacao', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
  follow_up: { label: 'Follow-up', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300' }
}

const emptyCampaignForm = {
  name: '',
  subject: '',
  channel: 'email',
  html_content: '',
  scheduled_for: '',
  filters: { sector: '', company_size: '', min_score: '' }
}

const emptyTemplateForm = {
  slug: '',
  name: '',
  description: '',
  subject: '',
  html_content: '',
  category: 'marketing',
  variables: ''
}

const MarketingCampaignsPage = () => {
  // --- shared state ---
  const [activeTab, setActiveTab] = useState('campaigns')
  const [loading, setLoading] = useState(false)
  const [brevoStatus, setBrevoStatus] = useState(null)

  // --- campaigns state ---
  const [campaigns, setCampaigns] = useState([])
  const [campaignFilter, setCampaignFilter] = useState('all')
  const [showCampaignModal, setShowCampaignModal] = useState(false)
  const [campaignForm, setCampaignForm] = useState({ ...emptyCampaignForm })
  const [savingCampaign, setSavingCampaign] = useState(false)

  // --- templates state ---
  const [templates, setTemplates] = useState([])
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [templateForm, setTemplateForm] = useState({ ...emptyTemplateForm })
  const [editingTemplateId, setEditingTemplateId] = useState(null)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templatePreview, setTemplatePreview] = useState(false)

  // --- metrics state ---
  const [metrics, setMetrics] = useState(null)

  // ============================================================
  // DATA FETCHING
  // ============================================================

  const loadBrevoStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/marketing/email/brevo/status')
      setBrevoStatus(res.data)
    } catch {
      setBrevoStatus({ connected: false })
    }
  }, [])

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true)
      const params = campaignFilter !== 'all' ? { status: campaignFilter } : {}
      const res = await api.get('/api/marketing/email/campaigns', { params })
      setCampaigns(res.data?.data || res.data || [])
    } catch (err) {
      console.error('Erro ao carregar campanhas:', err)
      toast.error('Erro ao carregar campanhas')
    } finally {
      setLoading(false)
    }
  }, [campaignFilter])

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true)
      const res = await api.get('/api/marketing/email/templates')
      setTemplates(res.data?.data || res.data || [])
    } catch (err) {
      console.error('Erro ao carregar templates:', err)
      toast.error('Erro ao carregar templates')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadMetrics = useCallback(async () => {
    try {
      const res = await api.get('/api/marketing/email/metrics')
      setMetrics(res.data?.data || res.data || null)
    } catch {
      setMetrics(null)
    }
  }, [])

  useEffect(() => {
    loadBrevoStatus()
    loadMetrics()
  }, [loadBrevoStatus, loadMetrics])

  useEffect(() => {
    if (activeTab === 'campaigns') loadCampaigns()
    if (activeTab === 'templates') loadTemplates()
  }, [activeTab, loadCampaigns, loadTemplates])

  // ============================================================
  // CAMPAIGN HANDLERS
  // ============================================================

  const handleCampaignFormChange = (field, value) => {
    setCampaignForm(prev => ({ ...prev, [field]: value }))
  }

  const handleCampaignFilterChange = (field, value) => {
    setCampaignForm(prev => ({
      ...prev,
      filters: { ...prev.filters, [field]: value }
    }))
  }

  const handleCreateCampaign = async (e) => {
    e.preventDefault()
    if (!campaignForm.name.trim() || !campaignForm.subject.trim()) {
      toast.error('Nome e assunto sao obrigatorios')
      return
    }
    try {
      setSavingCampaign(true)
      const payload = {
        ...campaignForm,
        filters: {
          sector: campaignForm.filters.sector || undefined,
          company_size: campaignForm.filters.company_size || undefined,
          min_score: campaignForm.filters.min_score ? Number(campaignForm.filters.min_score) : undefined
        }
      }
      await api.post('/api/marketing/email/campaigns', payload)
      toast.success('Campanha criada com sucesso')
      setShowCampaignModal(false)
      setCampaignForm({ ...emptyCampaignForm })
      loadCampaigns()
    } catch (err) {
      console.error('Erro ao criar campanha:', err)
      toast.error(err.response?.data?.error || 'Erro ao criar campanha')
    } finally {
      setSavingCampaign(false)
    }
  }

  const handleSendCampaign = async (campaign) => {
    const confirmed = window.confirm(
      `Tem certeza que deseja enviar a campanha "${campaign.name}"?\n\nEsta acao nao pode ser desfeita.`
    )
    if (!confirmed) return
    try {
      await api.post(`/api/marketing/email/campaigns/${campaign.id}/send`)
      toast.success('Campanha enviada para processamento')
      loadCampaigns()
      loadMetrics()
    } catch (err) {
      console.error('Erro ao enviar campanha:', err)
      toast.error(err.response?.data?.error || 'Erro ao enviar campanha')
    }
  }

  const handleDeleteCampaign = async (campaign) => {
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir a campanha "${campaign.name}"?`
    )
    if (!confirmed) return
    try {
      await api.delete(`/api/marketing/email/campaigns/${campaign.id}`)
      toast.success('Campanha excluida')
      loadCampaigns()
    } catch (err) {
      console.error('Erro ao excluir campanha:', err)
      toast.error(err.response?.data?.error || 'Erro ao excluir campanha')
    }
  }

  // ============================================================
  // TEMPLATE HANDLERS
  // ============================================================

  const handleTemplateFormChange = (field, value) => {
    setTemplateForm(prev => ({ ...prev, [field]: value }))
  }

  const openEditTemplate = (template) => {
    setEditingTemplateId(template.id)
    setTemplateForm({
      slug: template.slug || '',
      name: template.name || '',
      description: template.description || '',
      subject: template.subject || '',
      html_content: template.html_content || '',
      category: template.category || 'marketing',
      variables: Array.isArray(template.variables)
        ? template.variables.join(', ')
        : (template.variables || '')
    })
    setTemplatePreview(false)
    setShowTemplateModal(true)
  }

  const openNewTemplate = () => {
    setEditingTemplateId(null)
    setTemplateForm({ ...emptyTemplateForm })
    setTemplatePreview(false)
    setShowTemplateModal(true)
  }

  const handleSaveTemplate = async (e) => {
    e.preventDefault()
    if (!templateForm.name.trim() || !templateForm.slug.trim()) {
      toast.error('Nome e slug sao obrigatorios')
      return
    }
    try {
      setSavingTemplate(true)
      const payload = {
        ...templateForm,
        variables: templateForm.variables
          ? templateForm.variables.split(',').map(v => v.trim()).filter(Boolean)
          : []
      }
      if (editingTemplateId) {
        await api.put(`/api/marketing/email/templates/${editingTemplateId}`, payload)
        toast.success('Template atualizado com sucesso')
      } else {
        await api.post('/api/marketing/email/templates', payload)
        toast.success('Template criado com sucesso')
      }
      setShowTemplateModal(false)
      setTemplateForm({ ...emptyTemplateForm })
      setEditingTemplateId(null)
      loadTemplates()
    } catch (err) {
      console.error('Erro ao salvar template:', err)
      toast.error(err.response?.data?.error || 'Erro ao salvar template')
    } finally {
      setSavingTemplate(false)
    }
  }

  const handleDeleteTemplate = async (template) => {
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir o template "${template.name}"?`
    )
    if (!confirmed) return
    try {
      await api.delete(`/api/marketing/email/templates/${template.id}`)
      toast.success('Template excluido')
      loadTemplates()
    } catch (err) {
      console.error('Erro ao excluir template:', err)
      toast.error(err.response?.data?.error || 'Erro ao excluir template')
    }
  }

  const handleToggleTemplateActive = async (template) => {
    try {
      await api.put(`/api/marketing/email/templates/${template.id}`, {
        ...template,
        is_active: !template.is_active
      })
      toast.success(template.is_active ? 'Template desativado' : 'Template ativado')
      loadTemplates()
    } catch (err) {
      console.error('Erro ao alterar status do template:', err)
      toast.error('Erro ao alterar status do template')
    }
  }

  // ============================================================
  // HELPER RENDERERS
  // ============================================================

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    } catch {
      return dateStr
    }
  }

  const safeNumber = (val) => Number(val) || 0

  const calcRate = (numerator, denominator) => {
    const n = safeNumber(numerator)
    const d = safeNumber(denominator)
    if (d === 0) return '0.0'
    return ((n / d) * 100).toFixed(1)
  }

  // ============================================================
  // RENDER: BREVO STATUS
  // ============================================================

  const renderBrevoStatus = () => {
    const connected = brevoStatus?.connected
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
        connected
          ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      }`}>
        {connected ? (
          <>
            <SignalIcon className="w-4 h-4" />
            Brevo Conectado
          </>
        ) : (
          <>
            <SignalSlashIcon className="w-4 h-4" />
            Brevo Desconectado
          </>
        )}
      </div>
    )
  }

  // ============================================================
  // RENDER: METRICS
  // ============================================================

  const renderMetrics = () => {
    if (!metrics) return null

    const sent = safeNumber(metrics.total_sent || metrics.sent)
    const delivered = safeNumber(metrics.delivered || metrics.total_delivered)
    const opened = safeNumber(metrics.opened || metrics.total_opened)
    const clicked = safeNumber(metrics.clicked || metrics.total_clicked)
    const bounced = safeNumber(metrics.bounced || metrics.total_bounced)

    const kpis = [
      { label: 'Total Enviados', value: sent, icon: PaperAirplaneIcon, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
      { label: 'Entregues', value: delivered, icon: CheckCircleIcon, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20', rate: calcRate(delivered, sent), rateLabel: 'entrega' },
      { label: 'Abertos', value: opened, icon: EyeIcon, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20', rate: calcRate(opened, sent), rateLabel: 'abertura' },
      { label: 'Clicados', value: clicked, icon: ChartBarIcon, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', rate: calcRate(clicked, sent), rateLabel: 'clique' },
      { label: 'Bounced', value: bounced, icon: ExclamationCircleIcon, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', rate: calcRate(bounced, sent), rateLabel: 'bounce' }
    ]

    return (
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Metricas de Email
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {kpis.map((kpi) => {
            const Icon = kpi.icon
            return (
              <div key={kpi.label} className={`${kpi.bg} rounded-xl p-4 border border-gray-200 dark:border-gray-700`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${kpi.color}`} />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{kpi.label}</span>
                </div>
                <p className={`text-2xl font-bold ${kpi.color}`}>
                  {kpi.value.toLocaleString('pt-BR')}
                </p>
                {kpi.rate !== undefined && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Taxa de {kpi.rateLabel}: {kpi.rate}%
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ============================================================
  // RENDER: CAMPAIGNS TAB
  // ============================================================

  const renderCampaignsTab = () => (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Todos os status</option>
            {Object.entries(CAMPAIGN_STATUS_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>
          <button
            onClick={loadCampaigns}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Atualizar"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
        <button
          onClick={() => {
            setCampaignForm({ ...emptyCampaignForm })
            setShowCampaignModal(true)
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <PlusIcon className="w-5 h-5" />
          Nova Campanha
        </button>
      </div>

      {/* Campaign cards */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-20">
          <MegaphoneIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Nenhuma campanha encontrada</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Crie sua primeira campanha de marketing</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {campaigns.map((campaign) => {
            const statusCfg = CAMPAIGN_STATUS_CONFIG[campaign.status] || CAMPAIGN_STATUS_CONFIG.draft
            const channelCfg = CHANNEL_CONFIG[campaign.channel] || CHANNEL_CONFIG.email
            const ChannelIcon = channelCfg.icon
            const isDraft = campaign.status === 'draft'

            return (
              <div
                key={campaign.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {campaign.name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {campaign.subject}
                    </p>
                  </div>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusCfg.color}`}>
                    {statusCfg.label}
                  </span>
                </div>

                {/* Channel badge */}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${channelCfg.color}`}>
                    <ChannelIcon className="w-3.5 h-3.5" />
                    {channelCfg.label}
                  </span>
                  {campaign.scheduled_for && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      Agendada: {formatDate(campaign.scheduled_for)}
                    </span>
                  )}
                </div>

                {/* Metrics row */}
                <div className="grid grid-cols-3 gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 mb-3">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Enviados</p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      {safeNumber(campaign.sent_count || campaign.metrics?.sent).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Abertos</p>
                    <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {safeNumber(campaign.opened_count || campaign.metrics?.opened).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Clicados</p>
                    <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                      {safeNumber(campaign.clicked_count || campaign.metrics?.clicked).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {formatDate(campaign.created_at)}
                  </span>
                  <div className="flex items-center gap-1">
                    {isDraft && (
                      <>
                        <button
                          onClick={() => handleSendCampaign(campaign)}
                          className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                          title="Enviar campanha"
                        >
                          <PaperAirplaneIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCampaign(campaign)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Excluir campanha"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  // ============================================================
  // RENDER: TEMPLATES TAB
  // ============================================================

  const renderTemplatesTab = () => (
    <div>
      {/* Toolbar */}
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {templates.length} template{templates.length !== 1 ? 's' : ''} cadastrado{templates.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={openNewTemplate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <PlusIcon className="w-5 h-5" />
          Novo Template
        </button>
      </div>

      {/* Template grid */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-20">
          <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Nenhum template encontrado</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Crie seu primeiro template de email</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((template) => {
            const catCfg = TEMPLATE_CATEGORIES[template.category] || TEMPLATE_CATEGORIES.marketing
            return (
              <div
                key={template.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {template.name}
                    </h4>
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5">
                      {template.slug}
                    </p>
                  </div>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${catCfg.color}`}>
                    {catCfg.label}
                  </span>
                </div>

                {/* Subject */}
                {template.subject && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 truncate">
                    Assunto: {template.subject}
                  </p>
                )}

                {/* HTML preview */}
                <div className="flex-1 mb-3">
                  <div
                    className="w-full h-28 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden p-2 text-xs text-gray-500 dark:text-gray-400"
                    style={{ fontSize: '10px', lineHeight: '1.3' }}
                  >
                    {template.html_content ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: template.html_content.substring(0, 500)
                        }}
                        className="pointer-events-none"
                        style={{ transform: 'scale(0.6)', transformOrigin: 'top left', width: '166%' }}
                      />
                    ) : (
                      <span className="italic">Sem conteudo HTML</span>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                  {/* Active toggle */}
                  <button
                    onClick={() => handleToggleTemplateActive(template)}
                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${
                      template.is_active
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${template.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                    {template.is_active ? 'Ativo' : 'Inativo'}
                  </button>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditTemplate(template)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      title="Editar template"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template)}
                      className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title="Excluir template"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  // ============================================================
  // RENDER: CAMPAIGN MODAL
  // ============================================================

  const renderCampaignModal = () => {
    if (!showCampaignModal) return null
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Nova Campanha
            </h3>
            <button
              onClick={() => setShowCampaignModal(false)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleCreateCampaign} className="p-6 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome da Campanha *
              </label>
              <input
                type="text"
                value={campaignForm.name}
                onChange={(e) => handleCampaignFormChange('name', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Campanha de Renovacao Q1 2026"
                required
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assunto do Email *
              </label>
              <input
                type="text"
                value={campaignForm.subject}
                onChange={(e) => handleCampaignFormChange('subject', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Oferta especial para renovacao do seu contrato"
                required
              />
            </div>

            {/* Channel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Canal
              </label>
              <select
                value={campaignForm.channel}
                onChange={(e) => handleCampaignFormChange('channel', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="email">Email</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="multi">Multicanal</option>
              </select>
            </div>

            {/* HTML Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Conteudo HTML
              </label>
              <textarea
                value={campaignForm.html_content}
                onChange={(e) => handleCampaignFormChange('html_content', e.target.value)}
                rows={6}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                placeholder="<h1>Seu conteudo HTML aqui</h1>"
              />
            </div>

            {/* Scheduled for */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Agendar Envio (opcional)
              </label>
              <input
                type="datetime-local"
                value={campaignForm.scheduled_for}
                onChange={(e) => handleCampaignFormChange('scheduled_for', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filters section */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <FunnelIcon className="w-4 h-4" />
                Filtros de Destinatarios
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Setor</label>
                  <select
                    value={campaignForm.filters.sector}
                    onChange={(e) => handleCampaignFilterChange('sector', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Todos</option>
                    <option value="transportes">Transportes</option>
                    <option value="logistica">Logistica</option>
                    <option value="construcao">Construcao</option>
                    <option value="agronegocio">Agronegocio</option>
                    <option value="saude">Saude</option>
                    <option value="tecnologia">Tecnologia</option>
                    <option value="varejo">Varejo</option>
                    <option value="industria">Industria</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Porte da Empresa</label>
                  <select
                    value={campaignForm.filters.company_size}
                    onChange={(e) => handleCampaignFilterChange('company_size', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Todos</option>
                    <option value="micro">Micro (1-9)</option>
                    <option value="pequena">Pequena (10-49)</option>
                    <option value="media">Media (50-249)</option>
                    <option value="grande">Grande (250+)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Score Minimo</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={campaignForm.filters.min_score}
                    onChange={(e) => handleCampaignFilterChange('min_score', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowCampaignModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={savingCampaign}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {savingCampaign && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                )}
                Criar Campanha
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // ============================================================
  // RENDER: TEMPLATE MODAL
  // ============================================================

  const renderTemplateModal = () => {
    if (!showTemplateModal) return null
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {editingTemplateId ? 'Editar Template' : 'Novo Template'}
            </h3>
            <button
              onClick={() => {
                setShowTemplateModal(false)
                setEditingTemplateId(null)
              }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSaveTemplate} className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug *
                </label>
                <input
                  type="text"
                  value={templateForm.slug}
                  onChange={(e) => handleTemplateFormChange('slug', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_-]/g, ''))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  placeholder="ex: boas-vindas-lead"
                  required
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => handleTemplateFormChange('name', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Email de Boas-vindas"
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descricao
              </label>
              <input
                type="text"
                value={templateForm.description}
                onChange={(e) => handleTemplateFormChange('description', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Descricao breve do template"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assunto do Email
              </label>
              <input
                type="text"
                value={templateForm.subject}
                onChange={(e) => handleTemplateFormChange('subject', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Assunto que sera usado no envio"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Categoria
              </label>
              <select
                value={templateForm.category}
                onChange={(e) => handleTemplateFormChange('category', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(TEMPLATE_CATEGORIES).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>

            {/* HTML Content with preview toggle */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Conteudo HTML
                </label>
                <button
                  type="button"
                  onClick={() => setTemplatePreview(!templatePreview)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                >
                  <EyeIcon className="w-3.5 h-3.5" />
                  {templatePreview ? 'Editar' : 'Visualizar'}
                </button>
              </div>
              {templatePreview ? (
                <div
                  className="w-full min-h-[200px] border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 overflow-auto"
                  dangerouslySetInnerHTML={{ __html: templateForm.html_content || '<p style="color:#999">Sem conteudo para visualizar</p>' }}
                />
              ) : (
                <textarea
                  value={templateForm.html_content}
                  onChange={(e) => handleTemplateFormChange('html_content', e.target.value)}
                  rows={8}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  placeholder="<h1>Ola {{nome}}</h1><p>Conteudo do email...</p>"
                />
              )}
            </div>

            {/* Variables */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Variaveis (separadas por virgula)
              </label>
              <input
                type="text"
                value={templateForm.variables}
                onChange={(e) => handleTemplateFormChange('variables', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="nome, empresa, valor_contrato, data_vencimento"
              />
              {templateForm.variables && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {templateForm.variables.split(',').map((v, i) => {
                    const trimmed = v.trim()
                    if (!trimmed) return null
                    return (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-mono">
                        {`{{${trimmed}}}`}
                      </span>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowTemplateModal(false)
                  setEditingTemplateId(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={savingTemplate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {savingTemplate && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                )}
                {editingTemplateId ? 'Salvar Alteracoes' : 'Criar Template'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Campanhas de Marketing
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Gerencie campanhas de email, templates e acompanhe metricas de engajamento
          </p>
        </div>
        {renderBrevoStatus()}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex gap-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('campaigns')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
              activeTab === 'campaigns'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <ClipboardDocumentListIcon className="w-4 h-4" />
            Campanhas
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
              activeTab === 'templates'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <SwatchIcon className="w-4 h-4" />
            Templates Email
          </button>
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'campaigns' && renderCampaignsTab()}
      {activeTab === 'templates' && renderTemplatesTab()}

      {/* Metrics section (always visible) */}
      {renderMetrics()}

      {/* Modals */}
      {renderCampaignModal()}
      {renderTemplateModal()}
    </div>
  )
}

export default MarketingCampaignsPage
